// src/components/studentList/studentDetails/FeeManagementComponents/FeeAssignmentTable.js
import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    IconButton,
    InputAdornment,
    TextField as MuiTextField // Import TextField from Material UI
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import { formatCurrency } from '../../../utils/formattingUtils'; // Utility functions


const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    marginBottom: theme.spacing(3), // Reduced marginBottom for closer sections
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: theme.palette.grey[200],
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '1rem',
    textAlign: 'center', // Default to center for better readability in combined view
}));

// Define StyledTextField, using MuiTextField as the base
const StyledTextField = styled(MuiTextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: theme.palette.grey[400],
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.dark,
            borderWidth: 2,
        },
    },
}));

// Re-export StyledTextField as TextField to be used in the component
const TextField = StyledTextField;


const FeeAssignmentTable = ({ feeTypes, fees, isEditingFees, formErrors, handleFeeInputChange, totalAssignedFee }) => {

    if (feeTypes.length === 0) {
        return (
            <Typography variant="body1" color="textSecondary">
                No fee types available.
            </Typography>
        );
    }

    return (
        <Box>
            <StyledTableContainer component={Paper}>
                <Table aria-label="fee assignment table">
                    <StyledTableHead>
                        <TableRow>
                            <StyledTableCell>Fee Type</StyledTableCell>
                            <StyledTableCell align="right">Assigned Amount (₹)</StyledTableCell>
                            <StyledTableCell align="right">Discount (₹)</StyledTableCell>
                            <StyledTableCell align="right">Final Amount (₹)</StyledTableCell>
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {feeTypes.map((feeType) => {
                            const fee = fees.find((f) => f.feeTypeId === feeType.feeTypeId);
                            const feeAmount = parseFloat(fee?.fees) || 0;
                            const discountAmount = parseFloat(fee?.discount) || 0;
                            const finalAmount = Math.max(feeAmount - discountAmount, 0);

                            return (
                                <TableRow key={feeType.feeTypeId}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <Typography>{feeType.name}</Typography>
                                            <Tooltip title={feeType.description || 'No description'}>
                                                <IconButton size="small" aria-label="info">
                                                    <InfoIcon fontSize="small" color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField // Now TextField is correctly defined and available
                                            type="number"
                                            value={fee ? fee.fees : ''}
                                            onChange={(e) =>
                                                handleFeeInputChange(feeType.feeTypeId, 'fees', e.target.value)
                                            }
                                            variant="outlined"
                                            size="small"
                                            placeholder="Enter amount"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                inputProps: { min: 0 },
                                                readOnly: !isEditingFees,
                                            }}
                                            error={!!formErrors[`${feeType.feeTypeId}_fees`]}
                                            helperText={formErrors[`${feeType.feeTypeId}_fees`] || ''}
                                            fullWidth
                                            disabled={!isEditingFees}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField // Use TextField here as well
                                            type="number"
                                            value={fee ? fee.discount : ''}
                                            onChange={(e) =>
                                                handleFeeInputChange(feeType.feeTypeId, 'discount', e.target.value)
                                            }
                                            variant="outlined"
                                            size="small"
                                            placeholder="Enter discount"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                inputProps: { min: 0 },
                                                readOnly: !isEditingFees,
                                            }}
                                            error={!!formErrors[`${feeType.feeTypeId}_discount`]}
                                            helperText={formErrors[`${feeType.feeTypeId}_discount`] || ''}
                                            fullWidth
                                            disabled={!isEditingFees}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography>{formatCurrency(finalAmount)}</Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>

            {/* Display Total Assigned Fee in Table Footer */}
            <Box mt={2} display="flex" justifyContent="flex-end">
                <Typography variant="h6">
                    Total Fee: <strong>{formatCurrency(totalAssignedFee)}</strong>
                </Typography>
            </Box>
        </Box>
    );
};

export default FeeAssignmentTable;